import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getToken } from '../../../services/auth';
import RadiographyViewer from './RadiographyViewer';

interface Attachment {
  id: number;
  filename: string;
  content_type: string;
  tooth_number?: string;
  notes?: string;
  created_at: string;
  thumbnail_url?: string;
  download_url?: string;
}

interface Props {
  visitId: number;
  patientId?: string;
}

export default function PhotoGallery({ visitId }: Props) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!visitId) return;
    refreshAttachments();
  }, [visitId]);

  const refreshAttachments = () => {
    const headers = { Authorization: `Bearer ${getToken()}` };
    setLoading(true);
    axios.get(`/api/doctor/careflow360/${visitId}/attachments`, { headers })
      .then((r) => setAttachments(r.data?.attachments || r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleFolderUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const validExts = ['.jpg', '.jpeg', '.png', '.tif', '.tiff', '.dcm'];
    const imageFiles = Array.from(files).filter(f => validExts.some(ext => f.name.toLowerCase().endsWith(ext)));

    if (imageFiles.length === 0) {
      alert("No valid images found in selected folder.");
      return;
    }

    setUploading(true);
    const headers = { Authorization: `Bearer ${getToken()}` };
    let successCount = 0;

    for (const file of imageFiles) {
      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const payload = {
          filename: file.name,
          file_base64: base64,
          content_type: file.type || 'image/png',
          notes: 'Carestream RVG Bulk Upload'
        };

        await axios.post(`/api/doctor/careflow360/${visitId}/attachments`, payload, { headers });
        successCount++;
      } catch (err) {
        console.error("Failed to upload", file.name, err);
      }
    }
    
    setUploading(false);
    if (successCount > 0) refreshAttachments();
    
    // reset input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const images = attachments.filter((a) => (a.content_type || '').startsWith('image/'));
  if (loading) return <div style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>Loading photos...</div>;
  if (images.length === 0) return <div style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>No clinical photos</div>;

  const selected = selectedId != null ? images.find((a) => a.id === selectedId) : null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 12px' }}>
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#334155' }}>📸 Clinical Photos ({images.length})</h4>
        <button 
          onClick={() => fileInputRef.current?.click()} 
          disabled={uploading}
          style={{ 
            background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', 
            padding: '4px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: uploading ? 'wait' : 'pointer'
          }}
        >
          {uploading ? 'Uploading...' : '📁 Upload RVG Folder'}
        </button>
        {/* @ts-ignore - webkitdirectory is non-standard but widely supported */}
        <input type="file" ref={fileInputRef} webkitdirectory="true" directory="true" multiple style={{ display: 'none' }} onChange={handleFolderUpload} />
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
        {images.map((img) => (
          <div key={img.id} onClick={() => setSelectedId(img.id)}
            style={{
              borderRadius: 10, overflow: 'hidden', cursor: 'pointer', position: 'relative',
              border: selectedId === img.id ? '2px solid #8b5cf6' : '1px solid #e2e8f0',
              transition: 'all 0.2s', aspectRatio: '1',
            }}>
            <img
              src={img.thumbnail_url || img.download_url || ''}
              alt={img.filename}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            {img.tooth_number && (
              <div style={{
                position: 'absolute', bottom: 2, right: 2, background: 'rgba(0,0,0,0.6)',
                color: '#fff', fontSize: 10, padding: '1px 5px', borderRadius: 4,
              }}>#{img.tooth_number}</div>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox Viewer */}
      {selected && (
        <RadiographyViewer
          imageUrl={selected.download_url || selected.thumbnail_url || ''}
          filename={selected.filename}
          metadata={selected.tooth_number ? `Tooth #${selected.tooth_number} • ${new Date(selected.created_at).toLocaleDateString()}` : new Date(selected.created_at).toLocaleDateString()}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
